import * as React from 'react';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';
import NewJobPricelistItemData from './NewJobPricelistItem';
import { NewJobQuoteItem, ItemOptionList, NewJobQuoteFenceQuestions, EpaQuestions } from './models/JobInterfaces';
import { GenericList } from './models/JobSharedInterfaces';

interface NewJobPricelistEpaProps {
    isJobEvent: boolean;
    jobTypeId: number;
    customerId: number;
    accountTypeId: number;
    customerQuoteNotesDunny: string;
    scheduleDateDefaultDays: number;
    schedulePickup: boolean;
    allowAllocateToPastDate: boolean;
    jobInstalled: boolean;
    quoteItems: NewJobQuoteItem[];
    itemOptions: ItemOptionList[];
    frequencyOptions: GenericList[];
    yesNoOptions: GenericList[];
    epaExtra30MinOnSiteFeeFormatted: string;
    weekendFeeFormatted: string;
    emergencyFeeFormatted: string;
    truckHireRate: number;
    fenceQuestions: NewJobQuoteFenceQuestions;
    epaQuestions: EpaQuestions;
    customerChargeFuelSurcharge: boolean;
    chargeFuelSurchargeSwitchedOn: boolean;
    errors: { [key: string]: string };
    clearError(errorName: string): void;
    addItem(quoteItem: NewJobQuoteItem, calculatePrice: boolean): void;
    deleteItem(itemIndex: number): void;
    updateQuoteItem(quoteItem: NewJobQuoteItem, calculatePrice: boolean): void;
    updateEpaQuestions(epaQuestions: EpaQuestions): void;
}

const NewJobPricelistEpaData = (props: NewJobPricelistEpaProps) => {
    const [calcEpaOtherPrice, setCalcEpaOtherPrice] = React.useState(false);

    React.useEffect(() => {
        if (calcEpaOtherPrice) {
            var epaOtherQuoteItems = props.quoteItems.filter(i => i.itemTypeId === 61);
            for (var i = 0; i < epaOtherQuoteItems.length; i++) {
                var epaOtherItem = epaOtherQuoteItems[i];
                calculateEpaOtherPrice(epaOtherItem, props.epaQuestions);
            }
            setCalcEpaOtherPrice(false);
        }
    }, [calcEpaOtherPrice]);

    const handleEpaQuestionChange = (e: any) => {
        props.clearError(e.target.name);
        var value = e.target.value * 1;
        var epaQuestions = props.epaQuestions;

        if (e.target.name === "truckAccessToTank" || e.target.name === "steepDriveway") {
            epaQuestions.hoseMetres = 0;
        } else if (e.target.name === "tankLidLocationKnown") {
            epaQuestions.tankLidLocation = "";
        }

        let change: any = {};
        change = epaQuestions;
        change[e.target.name] = value;
        props.updateEpaQuestions(change);
    }

    const handleEpaQuestionWithFeeChange = (e: any) => {
        props.clearError(e.target.name);
        var value = e.target.value * 1;
        var epaQuestions = props.epaQuestions;

        if (e.target.name === "weekendJob") {
            epaQuestions.emergencySameDayJob = -1;
        } 

        let change: any = {};
        change = epaQuestions;
        change[e.target.name] = value;
        props.updateEpaQuestions(change);

        //need to recalc the price for each EPA item as added callout fees
        for (var i = 0; i < props.quoteItems.length; i++) {
            var item = props.quoteItems[i];

            props.updateQuoteItem(item, true);
        }
    }

    const handleEpaQuestionOther = (e: any) => {
        //EPA other doesn't have a pricelist so just need to add to entered price
        props.clearError(e.target.name);
        var value = e.target.value * 1;
        var epaQuestions = props.epaQuestions;

        let change: any = {};
        change = epaQuestions;
        change[e.target.name] = value;
        props.updateEpaQuestions(change);

        setCalcEpaOtherPrice(true);
    }

    const calculateEpaOtherPrice = async(epaOtherItem: NewJobQuoteItem, epaQuestions: EpaQuestions) => {
        const token = await authService.getAccessToken();

        let confinedSpace = epaQuestions.confinedSpace === 1;
        let weekendJob = epaQuestions.weekendJob === 1;
        let emergencyJob = epaQuestions.emergencySameDayJob === 1;
        let chargeFuelSurcharge = props.chargeFuelSurchargeSwitchedOn && props.customerChargeFuelSurcharge;   //customer must be set to charge fuel and fuel charge needs to be on

        axios.get('api/Job/CalculateEpaOtherJobPrice?CustomerId=' + props.customerId + '&ItemId=' + epaOtherItem.itemId + '&InitialHireAmount=' + epaOtherItem.initialHire + '&TravelAmount=' + epaOtherItem.travel + "&TruckHours=" + epaOtherItem.hours + "&ConfinedSpace=" + confinedSpace + "&ChargeWeekendJob=" + weekendJob + "&ChargeEmergencyJob=" + emergencyJob + "&ChargeFuelSurcharge=" + chargeFuelSurcharge, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            epaOtherItem.initialHire = res.data.initialHire;
            epaOtherItem.trench = res.data.truckHire;
            epaOtherItem.confinedSpace = res.data.confinedSpace;
            epaOtherItem.cleaning = res.data.callout
            epaOtherItem.travel = res.data.travel;
            epaOtherItem.fuelSurcharge = res.data.fuelSurcharge;
            epaOtherItem.amount = res.data.amount;
            epaOtherItem.calculatedPriceNote = res.data.calculatedPriceNote;
            props.updateQuoteItem(epaOtherItem, false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const handleEpaQuestionTankLidExposed = (e: any) => {
        props.clearError(e.target.name);
        var value = e.target.value * 1;
        var epaQuestions = props.epaQuestions;

        if (e.target.name === "tankLidExposed") {
            epaQuestions.tankLidLocationKnown = -1;
            epaQuestions.tankLidLocation = "";
        }

        let change: any = {};
        change = epaQuestions;
        change[e.target.name] = value;
        props.updateEpaQuestions(change);

        //need to recalc the price for septics or treatment plants
        var septics = props.quoteItems.filter(i => i.itemTypeId === 58);
        for (var i = 0; i < septics.length; i++) {
            var item = props.quoteItems[i];
            item.hours = value === 1 ? 0 : 0.5;

            props.updateQuoteItem(item, true);
        }
    }

    const handleEpaQuestionLidLocationChange = (e: any) => {
        props.clearError("tankLidLocation");
        var lidLocation = e.target.value;
        var epaQuestons = props.epaQuestions;
        epaQuestons.tankLidLocation = lidLocation;
        props.updateEpaQuestions(epaQuestons);
    }

    const handleLastServiceChange = (e: any) => {
        props.clearError("lastService");
        var lastService = e.target.value;
        var epaQuestons = props.epaQuestions;
        epaQuestons.lastService = lastService;
        props.updateEpaQuestions(epaQuestons);
    }

    const handleWaterBoardChange = (e: any) => {
        props.clearError("waterBoard");
        var waterBoard = e.target.value;
        var epaQuestons = props.epaQuestions;
        epaQuestons.waterBoard = waterBoard;
        props.updateEpaQuestions(epaQuestons);
    }

    const pricelistItem = <NewJobPricelistItemData isJobEvent={props.isJobEvent} jobTypeId={props.jobTypeId} scheduleDateDefaultDays={props.scheduleDateDefaultDays} schedulePickup={props.schedulePickup}
        lunchRoomExtraId={0} siteOfficeExtraId={0} showLocation={false} showExtras={true} showCleaning={true} showBracing={false} showMaintenance={true} showTruck={false} showEpaItems={true} showAdditionalHire={false}
        customerPickup={-1} allowAllocateToPastDate={props.allowAllocateToPastDate} jobInstalled={props.jobInstalled} accountTypeId={props.accountTypeId} quoteItems={props.quoteItems} itemOptions={props.itemOptions}
        truckHireRate={props.truckHireRate} fenceQuestions={props.fenceQuestions} frequencyOptions={props.frequencyOptions} extraItemOptions={[]} yesNoOptions={[]} truckOptions={[]} truckSideOptions={[]}
        chargeFuelSurchargeSwitchedOn={props.chargeFuelSurchargeSwitchedOn} customerChargeFuelSurcharge={props.customerChargeFuelSurcharge}
        addItem={props.addItem} deleteItem={props.deleteItem} updateQuoteItem={props.updateQuoteItem} />

    const isSeptic = props.quoteItems.filter(i => i.itemTypeId === 58).length > 0;
    const isGrease = props.quoteItems.filter(i => i.itemTypeId === 59).length > 0;
    const isOther = props.quoteItems.filter(i => i.itemTypeId === 61).length > 0;
    const maintQuestion = props.quoteItems.filter(i => i.itemTypeId >= 58 && i.itemTypeId <= 60).length > 0;
    const hasItems = props.quoteItems.length > 1;

    return (
        <div>
            <div className="newJobPricelist--customer-notes">{props.customerQuoteNotesDunny}</div>

            <div className="newJobPricelist--customer-notes">Inform the customer that if the size of the tank is larger than specified or the job takes more than an hour there will be extra charges.</div>
            <div className="newJobPricelist--customer-notes">Inform the customer that they are responsible for product until receiving disposal company takes it.</div>
            {pricelistItem}

            {props.errors["epaQuoteItems"] ?
                (props.errors["epaQuoteItems"]).split("\n").map((item: any, key: any) => {
                    return <span className="label errors" key={key}>{item}<br /></span>
                })
                : ""}
            <div className={isSeptic ? "" : "hidden"}>
                <label className="input-group marginBottom10" htmlFor="tankLidExposed">
                    <span className="label-xlarge">Is the lid of the tank exposed?</span>
                    <select className="select" id="tankLidExposed" name="tankLidExposed" value={props.epaQuestions.tankLidExposed} onChange={(e) => handleEpaQuestionTankLidExposed(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["tankLidExposed"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["tankLidExposed"]}</span>
                <div className={props.epaQuestions.tankLidExposed === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Ask customer to email a photo of the location of the lid</div>

                <label className={props.epaQuestions.tankLidExposed === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="tankLidLocationKnown">
                    <span className="label-xlarge">Do you know where the tank lid is located?</span>
                    <select className="select" id="tankLidLocationKnown" name="tankLidLocationKnown" value={props.epaQuestions.tankLidLocationKnown} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["tankLidLocationKnown"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["tankLidLocationKnown"]}</span>

                <label className={props.epaQuestions.tankLidLocationKnown === 1 ? "input-group marginBottom10" : "hidden"} htmlFor="tankLidLocation">
                    <span className="label label-xlarge">Location of Lid</span>
                    <input className="input" type="text" id="tankLidLocation" name="tankLidLocation" value={props.epaQuestions.tankLidLocation} onChange={(e) => handleEpaQuestionLidLocationChange(e)}></input>
                </label>
                <span className={props.errors["tankLidLocation"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["tankLidLocation"]}</span>
                <div className={props.epaQuestions.tankLidExposed === 0 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Let the customer know there is a {props.epaExtra30MinOnSiteFeeFormatted} extra charge per 30 mins to locate the lid of the tank</div>

                <label className="input-group marginBottom10" htmlFor="truckLargeAccess">
                    <span className="label-xlarge">Is there access for a large truck?<br></br>(3.5h x 1.8w x 6.2l)</span>
                    <select className="select" id="truckLargeAccess" name="truckLargeAccess" value={props.epaQuestions.truckLargeAccess} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["truckLargeAccess"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["truckLargeAccess"]}</span>

                <label className={props.epaQuestions.truckLargeAccess === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="truckSmallAccess">
                    <span className="label-xlarge">Is there access for a small truck?<br></br>(2.4h x 1.8w x 3.6l)</span>
                    <select className="select" id="truckSmallAccess" name="truckSmallAccess" value={props.epaQuestions.truckSmallAccess} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["truckSmallAccess"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["truckSmallAccess"]}</span>

                <label className="input-group marginBottom10" htmlFor="truckAccessToTank">
                    <span className="label-xlarge">Can a truck get within 25 metres of the septic tank?</span>
                    <select className="select" id="truckAccessToTank" name="truckAccessToTank" value={props.epaQuestions.truckAccessToTank} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["truckAccessToTank"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["truckAccessToTank"]}</span>

                <label className="input-group marginBottom10" htmlFor="steepDriveway">
                    <span className="label-xlarge">Is the driveway steep?</span>
                    <select className="select" id="steepDriveway" name="steepDriveway" value={props.epaQuestions.steepDriveway} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["steepDriveway"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["steepDriveway"]}</span>

                <label className={props.epaQuestions.truckAccessToTank === 0 || props.epaQuestions.steepDriveway === 1 ? "input-group" : "hidden"} htmlFor="hoseMetres">
                    <span className="label label-xlarge">Enter # metres of Hose required</span>
                    <input className="input customer__invDays" type="number" min="0" step="1" id="hoseMetres" name="hoseMetres" value={props.epaQuestions.hoseMetres} onChange={(e) => handleEpaQuestionChange(e)}></input>
                </label>
                <span className={props.errors["hoseMetres"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["hoseMetres"]}</span>

                <label className="input-group marginBottom10" htmlFor="sealedDriveway">
                    <span className="label-xlarge">Is the driveway sealed?</span>
                    <select className="select" id="sealedDriveway" name="sealedDriveway" value={props.epaQuestions.sealedDriveway} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["sealedDriveway"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["sealedDriveway"]}</span>
            </div>

            <div className={isGrease ? "" : "hidden"}>
                <label className="input-group marginBottom10" htmlFor="waterBoard">
                    <span className="label label-xlarge">Water Board that manages Grease Trap</span>
                    <input className="input" type="text" id="waterBoard" name="waterBoard" value={props.epaQuestions.waterBoard} onChange={(e) => handleWaterBoardChange(e)}></input>
                </label>
                <span className={props.errors["waterBoard"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["waterBoard"]}</span>
            </div>

            <label className="input-group marginBottom10" htmlFor="waterAccessOnSite">
                <span className="label-xlarge">Is there access to water on site via a garden tap?</span>
                <select className="select" id="waterAccessOnSite" name="waterAccessOnSite" value={props.epaQuestions.waterAccessOnSite} onChange={(e) => handleEpaQuestionChange(e)}>
                    <option hidden defaultValue="-1"></option>
                    {props.yesNoOptions.map(yn =>
                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                    )};
                </select>
            </label>
            <span className={props.errors["waterAccessOnSite"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["waterAccessOnSite"]}</span>

            <div className={maintQuestion ? "" : "hidden" }>
                <label className="input-group" htmlFor="lastService">
                    <span className="label label-xlarge">When was the Last Service (# months/years)</span>
                    <input className="input" type="text" id="lastService" name="lastService" value={props.epaQuestions.lastService} onChange={(e) => handleLastServiceChange(e)}></input>
                </label>
                <span className={props.errors["lastService"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["lastService"]}</span>
                <div className="marginLeftLabelxLarge makeitbold makeitred marginBottom10">Let the customer know there may be extra charges if any blockages</div>
            </div>

            <div className={isOther ? "" : "hidden"}>
                <label className="input-group marginBottom10" htmlFor="siteVisit">
                    <span className="label-xlarge">Do you require a Site Visit?</span>
                    <select className="select" id="siteVisit" name="siteVisit" value={props.epaQuestions.siteVisit} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["siteVisit"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["siteVisit"]}</span>

                <label className="input-group marginBottom10" htmlFor="confinedSpace">
                    <span className="label-xlarge">Is there a confined space?</span>
                    <select className="select" id="confinedSpace" name="confinedSpace" value={props.epaQuestions.confinedSpace} onChange={(e) => handleEpaQuestionOther(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["confinedSpace"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["confinedSpace"]}</span>

                <label className="input-group marginBottom10" htmlFor="materialSafetyDataSheet">
                    <span className="label-xlarge">Do you have a Material Safety Data Sheet (MSDS)?</span>
                    <select className="select" id="materialSafetyDataSheet" name="materialSafetyDataSheet" value={props.epaQuestions.materialSafetyDataSheet} onChange={(e) => handleEpaQuestionOther(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["materialSafetyDataSheet"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["materialSafetyDataSheet"]}</span>
                <div className={props.epaQuestions.materialSafetyDataSheet === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : ""}>Ask the customer to email the MSDS</div>

                <label className={props.epaQuestions.materialSafetyDataSheet === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="sampleTestingRequired">
                    <span className="label-xlarge">Is Sample Testing Reqired?</span>
                    <select className="select" id="sampleTestingRequired" name="sampleTestingRequired" value={props.epaQuestions.sampleTestingRequired} onChange={(e) => handleEpaQuestionOther(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["sampleTestingRequired"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["sampleTestingRequired"]}</span>

                <div className="marginLeftLabelxLarge makeitbold makeitred">Let the customer know there will be extra charges for {props.epaQuestions.materialSafetyDataSheet === 1 ? "" : "sample testing and"} waste disposal</div>
            </div>

            <div className={hasItems ? "" : "hidden"}>
                <label className="input-group marginBottom10" htmlFor="weekendJob">
                    <span className="label-xlarge">Is this a weekend, public holiday, or after 5PM job?</span>
                    <select className="select" id="weekendJob" name="weekendJob" value={props.epaQuestions.weekendJob} onChange={(e) => isOther ? handleEpaQuestionOther(e) : handleEpaQuestionWithFeeChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["weekendJob"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["weekendJob"]}</span>
                <div className={props.epaQuestions.weekendJob === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Let the customer know there is a {props.weekendFeeFormatted} fee for weekend jobs.</div>

                <label className={props.epaQuestions.weekendJob === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="emergencySameDay">
                    <span className="label-xlarge">Is this an emergency / same day job?</span>
                    <select className="select" id="emergencySameDayJob" name="emergencySameDayJob" value={props.epaQuestions.emergencySameDayJob} onChange={(e) => isOther ? handleEpaQuestionOther(e) : handleEpaQuestionWithFeeChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={props.errors["emergencySameDayJob"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{props.errors["emergencySameDayJob"]}</span>
                <div className={props.epaQuestions.emergencySameDayJob === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Let the customer know there is a {props.emergencyFeeFormatted} fee for an emergency / same day job</div>
            </div>
        </div>
    )

}
 
export default NewJobPricelistEpaData;